import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useImperativeHandle,
  useMemo,
} from 'react';
import {
  TextField,
  IconButton,
  Box,
  Tooltip,
  Button,
  Typography,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Alert,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import styles from './ChatInput.module.scss';
import SendIcon from '@mui/icons-material/Send';
import AddIcon from '@mui/icons-material/Add';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import LinkIcon from '@mui/icons-material/Link';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CloseIcon from '@mui/icons-material/Close';
import { useUntypedTranslation } from '../../hooks/useUntypedTranslation';
import SpeechRecordingButton from './SpeechRecordingButton';
import DocumentUpload from './DocumentUpload';
import { useDocumentUpload } from '../../hooks/useDocumentUpload';
import { ThinkingMode } from '../../redux/slices/chat/chatTypes';
import { isLegacyConversationType } from '../../redux/slices/chat/chatConfig';
import { hasCriticalMessageFailed } from '../../utils/messageUtils';
import { MessageAttachment } from '../../models/Message';
import { extractURLsFromText, isValidURL } from '../../types/documentUpload';
import { markDocumentsAsDeleted } from '../../services/documentUploadService';

/**
 * FEATURE FLAG: URL Detection
 *
 * Status: DISABLED - Waiting for backend URL scraping implementation
 *
 * @see ChatPage.tsx for corresponding flag
 */
const ENABLE_URL_DETECTION = false;

/**
 * Imperative handle exposed to parent via forwardRef.
 * Allows ChatPage to clear or set the input without owning its state,
 * which prevents ChatPage re-renders on every keystroke.
 */
export interface ChatInputHandle {
  clear: () => void;
  setValue: (value: string) => void;
}

interface ChatInputProps {
  loading: boolean;
  conversationId: string;

  // Thinking mode approach
  currentThinkingMode: ThinkingMode;
  onThinkingModeChange: (mode: ThinkingMode) => void;
  isFirstMessage: boolean;

  // messageText is the current input value, passed by ChatInput at the moment of send
  handleSendMessage: (
    messageText: string,
    customParams?: { project_id?: string; version_id?: string }
  ) => void;
  onStartNewChat?: () => void;

  // Message ID for attachments (pre-generated, never null)
  currentMessageId: string;

  // Attachment handling
  attachments: MessageAttachment[];
  onAttachmentsChange: (attachments: MessageAttachment[]) => void;

  // Raw File objects for multipart upload (new conversation with documents)
  onPendingFilesChange?: (files: File[]) => void;
}

const ChatInput = React.forwardRef<ChatInputHandle, ChatInputProps>(
  (
    {
      loading,
      conversationId,
      currentThinkingMode,
      onThinkingModeChange,
      isFirstMessage,
      handleSendMessage,
      onStartNewChat,
      currentMessageId,
      attachments,
      onAttachmentsChange,
      onPendingFilesChange,
    },
    ref
  ) => {
    const { t } = useUntypedTranslation();

    // Input state lives here, not in ChatPage — typing only re-renders ChatInput,
    // not the entire ChatPage tree (which includes ChatMessages with N messages).
    const [messageInput, setMessageInput] = useState('');

    // Expose clear/setValue so ChatPage can reset the field when switching
    // conversations or starting a new chat, without owning the state itself.
    useImperativeHandle(ref, () => ({
      clear: () => setMessageInput(''),
      setValue: (value: string) => setMessageInput(value),
    }));

    // Dev mode detection (similar to staging check in Header)
    // const isDev = process.env.REACT_APP_FIREBASE_PROJECT_ID === 'gaialogic-staging' || process.env.NODE_ENV === 'development';
    // TODO: Decide if we need DEV mode or if we remove it completely, for now set it to false
    const isDev = false;

    // State for custom prompt graph parameters (dev mode only)
    const [customProjectId, setCustomProjectId] = useState('hegel_complete_outputs_920ae661');
    const [customVersionId, setCustomVersionId] = useState('Px09vkoGJB667heMn8mB');

    // State for document upload dialog
    const [showUploadDialog, setShowUploadDialog] = useState(false);
    const [showBeforeUnloadWarning, setShowBeforeUnloadWarning] = useState(false);

    // State for speech recording
    const [isRecordingActive, setIsRecordingActive] = useState(false);

    // State for current time (updates every minute to check timeouts)
    const [nowTs, setNowTs] = useState(Date.now());

    // Update current time every minute for timeout detection
    useEffect(() => {
      const interval = setInterval(() => setNowTs(Date.now()), 60_000);
      return () => clearInterval(interval);
    }, []);

    // Get current user for document uploads
    const currentUser = useSelector((state: RootState) => state.user.user);

    // Document upload hook
    const {
      uploadState,
      fileStatuses,
      isUploading,
      totalProgress,
      selectFiles,
      removeFile,
      startUpload,
      cancelUpload,
      clearFiles,
      validateFiles,
    } = useDocumentUpload(conversationId, currentMessageId, isFirstMessage);

    // Optional: if you ever need to update local state when the conversation changes,
    // you can add an effect like this:

    useEffect(() => {
      // Reset any local state if needed here
      // Clear files when conversation changes
      clearFiles();
    }, [conversationId, clearFiles]);

    // 1) Look up the conversation-specific status from Redux
    const conversationState = useSelector(
      (state: RootState) => state.chat?.conversationStates?.[conversationId]
    ) || {
      status: 'idle',
      progress: '',
    };

    // Look up the current conversation to determine its type
    const currentConversation = useSelector((state: RootState) =>
      state.chat.conversations.find(c => c.id === conversationId)
    );

    // Get conversation thinkingType (if it exists)
    const conversationThinkingType = currentConversation?.thinkingType;

    // Check if this is a legacy conversation (read-only)
    const isLegacyConversation = isLegacyConversationType(conversationThinkingType);

    // Get all messages for the current conversation to check for critical message failures
    const messages = useSelector((state: RootState) => state.chat.messages);

    // Check if the first prompt-graph message failed.
    // It sets up the RAG context - if it fails, the conversation is unusable.
    // Memoized so the linear scan does not run on every keystroke.
    const criticalMessageFailed = useMemo(
      () => hasCriticalMessageFailed(messages, nowTs),
      [messages, nowTs]
    );

    // 2) Decide if the send button should be disabled
    const isDisabled =
      loading ||
      !messageInput.trim() ||
      conversationState.status === 'busy' ||
      isLegacyConversation ||
      isUploading ||
      isRecordingActive;

    // Extract URLs from message input (only if feature is enabled)
    const detectedURLs = ENABLE_URL_DETECTION ? extractURLsFromText(messageInput) : [];

    // Handle page refresh warning during upload
    useEffect(() => {
      const shouldShowWarning = isUploading;
      setShowBeforeUnloadWarning(shouldShowWarning);

      if (shouldShowWarning) {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
          e.preventDefault();
          e.returnValue = t(
            'documentUpload.pageLeaveWarning',
            'Files are being uploaded. Are you sure you want to leave?'
          );
          return e.returnValue;
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
          window.removeEventListener('beforeunload', handleBeforeUnload);
        };
      }
    }, [isUploading, t]);

    // Handle file upload completion
    useEffect(() => {
      if (fileStatuses.length > 0 && !isUploading) {
        // All uploads completed, create attachment objects
        const doneFiles = fileStatuses.filter(f => f.status === 'done');
        const newAttachments: MessageAttachment[] = doneFiles.map(f => ({
          id: f.id,
          type: 'document' as const,
          name: f.name,
          size: f.size,
          mimeType: f.mimeType,
          storagePath: f.storagePath,
          status: 'done' as const,
        }));

        if (newAttachments.length > 0) {
          onAttachmentsChange([...attachments, ...newAttachments]);

          // For new conversations, also pass raw File objects for multipart upload
          if (isFirstMessage && onPendingFilesChange) {
            const rawFiles = doneFiles.map(f => f.file).filter((file): file is File => !!file);
            onPendingFilesChange(rawFiles);
          }

          clearFiles();
          setShowUploadDialog(false);
        }
      }
    }, [
      fileStatuses,
      isUploading,
      attachments,
      onAttachmentsChange,
      clearFiles,
      isFirstMessage,
      onPendingFilesChange,
    ]);

    const handleTextFieldChange = (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      setMessageInput(event.target.value);
    };

    const handleLocalKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter' && event.shiftKey) {
        // Prevent Shift+Enter from inserting a newline (consistent with previous behavior).
        event.preventDefault();
      }
    };

    /**
     * Handles transcribed text from speech recording
     */
    const handleTranscribedText = (text: string) => {
      setMessageInput(text);
    };

    /**
     * Open document upload dialog
     */
    const handleOpenUploadDialog = () => {
      // Cancel speech recording if active
      // This is handled by the SpeechRecordingButton component itself
      setShowUploadDialog(true);
    };

    /**
     * Remove an attachment
     */
    const handleRemoveAttachment = async (attachmentId: string) => {
      // Find the attachment
      const attachment = attachments.find(a => a.id === attachmentId);

      // If it's a document type, mark as deleted in Firestore
      if (attachment && attachment.type === 'document' && currentUser?.id) {
        try {
          await markDocumentsAsDeleted(currentUser.id, [attachmentId]);
          console.log(`[ChatInput] Marked removed attachment as deleted: ${attachmentId}`);
        } catch (error) {
          console.error('[ChatInput] Failed to mark attachment as deleted:', error);
          // Don't block UI if this fails
        }
      }

      // Remove from local state
      onAttachmentsChange(attachments.filter(a => a.id !== attachmentId));
    };

    /**
     * Handle upload dialog close
     */
    const handleCloseUploadDialog = () => {
      if (!isUploading) {
        setShowUploadDialog(false);
        clearFiles();
      }
    };

    // If this is a legacy conversation, show read-only notice
    if (isLegacyConversation) {
      return (
        <div className={styles.chatInput}>
          <div className={styles.chatInput__container}>
            <Box
              sx={{
                p: 3,
                backgroundColor: 'grey.50',
                borderRadius: 2,
                textAlign: 'center',
                border: '1px solid',
                borderColor: 'grey.300',
              }}
            >
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {t('chat.archivedConversationTitle', 'Archived Conversation')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t(
                  'chat.archivedConversationReadOnly',
                  'This conversation is archived and read-only. Start a new conversation to continue.'
                )}
              </Typography>
              {onStartNewChat && (
                <Button variant="contained" onClick={onStartNewChat} startIcon={<AddIcon />}>
                  {t('chat.startNewConversation', 'Start New Conversation')}
                </Button>
              )}
            </Box>
          </div>
        </div>
      );
    }

    // If the first prompt-graph message failed, block input and show recovery option
    if (criticalMessageFailed) {
      return (
        <div className={styles.chatInput}>
          <div className={styles.chatInput__container}>
            <Box
              sx={{
                p: 2.5,
                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                borderRadius: 2,
                textAlign: 'center',
                border: '1px solid',
                borderColor: 'rgba(0, 0, 0, 0.08)',
              }}
            >
              <Typography variant="body1" color="text.secondary" sx={{ mb: 1.5, fontWeight: 500 }}>
                {t('chat.unableToProcess', 'Unable to process your request. Please try again.')}
              </Typography>
              {onStartNewChat && (
                <Button
                  variant="contained"
                  onClick={onStartNewChat}
                  startIcon={<AddIcon />}
                  sx={{ textTransform: 'none' }}
                >
                  {t('chat.tryAgain', 'Try Again')}
                </Button>
              )}
            </Box>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.chatInput}>
        <div className={styles.chatInput__container}>
          {/* Dev Mode: Custom Prompt Graph Parameters - Above text field */}
          {isDev && currentThinkingMode === 'prompt-graph' && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                width: '100%',
                mb: 2,
                p: 2,
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                backgroundColor: '#f5f5f5',
              }}
            >
              <Box sx={{ fontSize: '0.875rem', fontWeight: 'bold', color: '#666' }}>
                Dev Mode: Prompt Graph Parameters
              </Box>
              <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                <TextField
                  label="Project ID"
                  variant="outlined"
                  size="small"
                  value={customProjectId}
                  onChange={e => setCustomProjectId(e.target.value)}
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="Version ID"
                  variant="outlined"
                  size="small"
                  value={customVersionId}
                  onChange={e => setCustomVersionId(e.target.value)}
                  sx={{ flex: 1 }}
                />
              </Box>
            </Box>
          )}

          {/* Attached files display */}
          {attachments.length > 0 && (
            <Box sx={{ mb: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {attachments.map(attachment => (
                <Chip
                  key={attachment.id}
                  label={attachment.name}
                  icon={attachment.type === 'url' ? <LinkIcon /> : <InsertDriveFileIcon />}
                  onDelete={() => handleRemoveAttachment(attachment.id)}
                  size="small"
                  color={attachment.status === 'error' ? 'error' : 'primary'}
                  variant="outlined"
                />
              ))}
            </Box>
          )}

          {/* URL detection display - only shown when feature is enabled */}
          {ENABLE_URL_DETECTION && detectedURLs.length > 0 && (
            <Alert severity="info" sx={{ mb: 1 }}>
              <Typography variant="caption">
                {t('chat.urlDetected', 'URL detected. It will be processed as context.')}
              </Typography>
            </Alert>
          )}

          <TextField
            fullWidth
            multiline
            maxRows={7}
            placeholder={t(
              isFirstMessage
                ? 'chat.inputPlaceholderPromptGraph'
                : 'chat.inputPlaceholderFastThinking'
            )}
            variant="outlined"
            value={messageInput}
            onChange={handleTextFieldChange}
            onKeyDown={handleLocalKeyDown}
            disabled={loading || conversationState.status === 'busy' || isUploading}
            className={styles.chatInput__textField}
          />

          {/* Document Upload Controls - Left Side */}
          <div className={styles.chatInput__controls}>
            {/* Document Upload Button */}
            <Tooltip title={t('chat.uploadDocuments')} placement="top">
              <span>
                <IconButton
                  color="primary"
                  onClick={handleOpenUploadDialog}
                  disabled={
                    loading ||
                    conversationState.status === 'busy' ||
                    isLegacyConversation ||
                    isRecordingActive
                  }
                  aria-label={t('chat.uploadDocuments')}
                >
                  <AttachFileIcon />
                </IconButton>
              </span>
            </Tooltip>

            {/* Send Controls - Right Side */}
            <div className={styles.chatInput__sendControls}>
              {/* Speech Recording Button */}
              <SpeechRecordingButton
                onTranscribedText={handleTranscribedText}
                onRecordingStateChange={setIsRecordingActive}
                disabled={loading || conversationState.status === 'busy' || isUploading}
                className={styles.chatInput__speechRecordingInSend}
              />
              <Tooltip title={t('chat.send')} placement="top">
                <span>
                  {' '}
                  {/* Span wrapper needed for disabled button tooltips */}
                  <IconButton
                    color="primary"
                    onClick={() =>
                      handleSendMessage(
                        messageInput,
                        isDev && currentThinkingMode === 'prompt-graph'
                          ? { project_id: customProjectId, version_id: customVersionId }
                          : undefined
                      )
                    }
                    disabled={isDisabled}
                    aria-label={t('chat.send')}
                  >
                    <SendIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </div>
          </div>
        </div>

        {/* Document Upload Dialog */}
        <Dialog
          open={showUploadDialog}
          onClose={handleCloseUploadDialog}
          maxWidth="sm"
          fullWidth
          disableEscapeKeyDown={isUploading}
          PaperProps={{
            sx: {
              backgroundColor: '#ffffff',
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(9, 13, 46, 0.12)',
            },
          }}
        >
          <DialogTitle
            sx={{
              backgroundColor: '#ffffff',
              borderBottom: '1px solid rgba(9, 13, 46, 0.08)',
              px: 3,
              py: 2,
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 500,
                  color: 'var(--color-ink-primary)',
                  fontSize: '1.05rem',
                }}
              >
                {t('documentUpload.title', 'Upload Documents')}
              </Typography>
              <IconButton
                edge="end"
                onClick={handleCloseUploadDialog}
                aria-label="close"
                disabled={isUploading}
                sx={{
                  color: 'var(--color-ink-tertiary)',
                  transition: 'color 150ms ease-out',
                  '&:hover': {
                    color: 'var(--color-ink-primary)',
                    backgroundColor: 'var(--color-sand)',
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ backgroundColor: '#ffffff', px: 3, py: 2.5 }}>
            <DocumentUpload
              files={fileStatuses}
              onSelectFiles={selectFiles}
              onRemoveFile={removeFile}
              onStartUpload={startUpload}
              isUploading={isUploading}
              disabled={loading || conversationState.status === 'busy'}
            />
          </DialogContent>
          {isUploading && (
            <DialogActions
              sx={{
                backgroundColor: '#ffffff',
                borderTop: '1px solid rgba(9, 13, 46, 0.08)',
                px: 3,
                py: 1.5,
              }}
            >
              <Button
                onClick={cancelUpload}
                color="error"
                variant="outlined"
                size="small"
                sx={{ borderRadius: '8px', textTransform: 'none' }}
              >
                {t('documentUpload.cancelUpload', 'Cancel Upload')}
              </Button>
            </DialogActions>
          )}
        </Dialog>
      </div>
    );
  }
);

ChatInput.displayName = 'ChatInput';

export default ChatInput;
